import { useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { FruitShopObserverView } from "./GameScreen";
import { FRUIT_SHOP_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import type { FruitShopReplaySnapshot } from "./replay-runner";
import { buildFruitShopReplaySnapshots } from "./replay-runner";

interface ReplayScreenProps {
  readonly scenario: string;
}

function actorName(
  snapshot: FruitShopReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
): string {
  return namesByPlayerId[snapshot.event.userId] ?? snapshot.event.userId;
}

function eventSummary(snapshot: FruitShopReplaySnapshot): string {
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = FRUIT_SHOP_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () =>
      fixture
        ? buildFruitShopReplaySnapshots(fixture.events, {
            observerUserId: fixture.observerUserId,
          })
        : [],
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(0);
  const current = snapshots[stepIndex] ?? snapshots[0] ?? null;
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const maxIndex = snapshots.length - 1;

  return (
    <div
      className="min-h-screen bg-surface-primary"
      data-testid="fruit-shop-v2-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="fruit-shop-v2-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm text-text-primary"
              data-testid="fruit-shop-v2-replay-event-summary"
            >
              {eventSummary(current)} · {actorName(current, namesByPlayerId)}
            </p>
            <p className="text-text-secondary text-xs">
              {current.viewerState.currentRound.phase} ·{" "}
              {current.viewerState.status}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="fruit-shop-v2-replay-prev"
              disabled={stepIndex === 0}
              onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={stepIndex}
              className="w-36 md:w-56"
              data-testid="fruit-shop-v2-replay-step-slider"
              onChange={(event) => setStepIndex(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="fruit-shop-v2-replay-next"
              disabled={stepIndex === maxIndex}
              onClick={() =>
                setStepIndex((index) => Math.min(maxIndex, index + 1))
              }
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="fruit-shop-v2-replay-step-count"
            >
              {stepIndex + 1}/{snapshots.length}
            </span>
          </div>
        </div>
      </div>

      <FruitShopObserverView
        viewerState={current.viewerState}
        connectionLabel="replay"
        getPlayerName={(playerId) => namesByPlayerId[playerId] ?? playerId}
        leaveLabel="닫기"
        onLeave={() => navigateTo("/")}
      />
    </div>
  );
}
